import React, { useState } from "react";
import { Field, ErrorMessage } from "formik";
import { get } from "lodash";
import {
  UploadCloud,
  X,
  GripVertical,
  Plus,
  AlertCircle,
  Loader2,
  Image as ImageIcon,
  Film,
  Eye,
  CheckSquare,
  Square,
} from "lucide-react";
import { IMAGE_URL } from "../../config";
import { formatUnderscoreToSpace } from "../../utils/text-format";

export const FormMultiImageVideoField = ({
  name = "media",
  label = "Media Upload",
  className = "",
  maxSizeMB = 100,
  ...props
}) => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [uploadStatuses, setUploadStatuses] = useState([]);
  const [isDragOverDropZone, setIsDragOverDropZone] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);

  const handleFileChange = async (eventOrFiles, setFieldValue, values) => {
    let files = [];

    if (eventOrFiles?.target?.files) {
      files = Array.from(eventOrFiles.target.files);
    } else if (
      eventOrFiles instanceof FileList ||
      Array.isArray(eventOrFiles)
    ) {
      files = Array.from(eventOrFiles);
    }

    if (files.length === 0) return;

    const MAX_SIZE = maxSizeMB * 1024 * 1024;
    const validFiles = files.filter((file) => file.size <= MAX_SIZE);

    if (validFiles.length === 0) return;

    const newStatuses = validFiles.map((file, idx) => ({
      id: `${Date.now()}-${idx}`,
      file,
      status: "loading",
      progress: 0,
      url: null,
      previewUrl: URL.createObjectURL(file),
      type: file.type.startsWith("video/") ? "video" : "image",
    }));

    setUploadStatuses((prev) => [...prev, ...newStatuses]);

    const uploadPromises = newStatuses.map(
      (status) =>
        new Promise((resolve) => {
          const formData = new FormData();
          formData.append("file", status.file);

          const xhr = new XMLHttpRequest();

          xhr.upload.addEventListener("progress", (e) => {
            if (e.lengthComputable) {
              const percentComplete = Math.round((e.loaded / e.total) * 100);
              setUploadStatuses((prev) =>
                prev.map((s) =>
                  s.id === status.id ? { ...s, progress: percentComplete } : s,
                ),
              );
            }
          });

          xhr.onreadystatechange = () => {
            if (xhr.readyState === 4) {
              if (xhr.status >= 200 && xhr.status < 300) {
                try {
                  const response = JSON.parse(xhr.responseText);
                  const url = response.data?.filePath || response.filePath;
                  const type = status.file.type.startsWith("video/")
                    ? "video"
                    : "image";

                  setUploadStatuses((prev) =>
                    prev.map((s) =>
                      s.id === status.id
                        ? { ...s, progress: 100, status: "done", url, type }
                        : s,
                    ),
                  );

                  resolve({ url, type, checked: false, alt: "" });
                } catch {
                  setUploadStatuses((prev) =>
                    prev.map((s) =>
                      s.id === status.id ? { ...s, status: "error" } : s,
                    ),
                  );
                  resolve(null);
                }
              } else {
                setUploadStatuses((prev) =>
                  prev.map((s) =>
                    s.id === status.id ? { ...s, status: "error" } : s,
                  ),
                );
                resolve(null);
              }
            }
          };

          xhr.open("POST", IMAGE_URL);
          xhr.send(formData);
        }),
    );

    const uploadedResults = await Promise.all(uploadPromises);
    const successfulUploads = uploadedResults.filter(Boolean);

    const currentMedia = get(values, name) || [];
    const updatedMedia = [...currentMedia, ...successfulUploads].map(
      (item, index) => ({
        ...item,
        index,
      }),
    );

    setFieldValue(name, updatedMedia);

    setUploadStatuses((prev) => prev.filter((s) => s.status !== "done"));

    if (eventOrFiles?.target) {
      eventOrFiles.target.value = "";
    }
  };

  const handleDelete = (index, setFieldValue, values) => {
    const currentMedia = get(values, name) || [];
    const newMedia = [...currentMedia];
    newMedia.splice(index, 1);
    setFieldValue(name, newMedia);
  };

  const handleDragStart = (index) => setDraggedIndex(index);
  const handleDragOverItem = (e) => e.preventDefault();

  const handleDropItem = (index, values, setFieldValue) => {
    if (draggedIndex === null || draggedIndex === index) return;
    const currentMedia = get(values, name) || [];
    const updatedItems = [...currentMedia];
    const draggedItem = updatedItems.splice(draggedIndex, 1)[0];
    updatedItems.splice(index, 0, draggedItem);
    setFieldValue(name, updatedItems);
    setDraggedIndex(null);
  };

  const handleTouchStart = (index) => setDragIndex(index);

  const handleTouchMove = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    const elements = document.querySelectorAll("[data-draggable-index]");

    let closestIndex = null;
    let closestDistance = Infinity;

    elements.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const index = Number(el.getAttribute("data-draggable-index"));
      const centerY = rect.top + rect.height / 2;
      const distance = Math.abs(touch.clientY - centerY);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    if (closestIndex !== null && closestIndex !== hoverIndex) {
      setHoverIndex(closestIndex);
    }
  };

  const handleTouchEnd = (values, setFieldValue) => {
    if (dragIndex !== null && hoverIndex !== null && dragIndex !== hoverIndex) {
      const currentMedia = get(values, name) || [];
      const newMedia = [...currentMedia];
      const [movedItem] = newMedia.splice(dragIndex, 1);
      newMedia.splice(hoverIndex, 0, movedItem);
      setFieldValue(name, newMedia);
    }
    setDragIndex(null);
    setHoverIndex(null);
  };

  const handleCheckBox = (index, setFieldValue, values, checked) => {
    const currentMedia = get(values, name) || [];
    const newMedia = [...currentMedia];
    newMedia[index] = { ...newMedia[index], checked };
    setFieldValue(name, newMedia);
  };

  const handleALTText = (index, setFieldValue, values, inputValue) => {
    const currentMedia = get(values, name) || [];
    const newMedia = [...currentMedia];
    newMedia[index] = { ...newMedia[index], alt: inputValue };
    setFieldValue(name, newMedia);
  };

  const handleDropzoneDrop = (e, setFieldValue, values) => {
    e.preventDefault();
    setIsDragOverDropZone(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileChange(e.dataTransfer.files, setFieldValue, values);
    }
  };

  return (
    <Field name={name}>
      {({ form, meta }) => {
        const values = form.values;
        const mediaItems = get(values, name) || [];
        const hasError = Boolean(meta.touched && meta.error);
        const activeUploads = uploadStatuses.filter((s) => s.status !== "done");

        return (
          <div className={`w-full ${className}`}>
            {/* Header / Section Title */}
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="text-sm font-semibold text-gray-800">
                  {formatUnderscoreToSpace(label)}
                </label>
                {mediaItems.length > 0 && (
                  <p className="text-xs text-gray-500">
                    {mediaItems.length}{" "}
                    {mediaItems.length === 1 ? "item" : "items"} uploaded · Drag
                    to reorder
                  </p>
                )}
              </div>

              {mediaItems.length > 0 && (
                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-primary hover:bg-indigo-700 active:bg-indigo-800 rounded-lg cursor-pointer transition shadow-xs">
                  <Plus className="w-4 h-4" />
                  Add <span className="hidden md:block">Media</span>
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    className="hidden"
                    onChange={(e) =>
                      handleFileChange(e, form.setFieldValue, values)
                    }
                  />
                </label>
              )}
            </div>

            {/* Main Outer Box */}
            <div
              className={`relative rounded-xl transition-colors ${
                hasError
                  ? "border-red-300 bg-red-50/20"
                  : "border-gray-200 bg-gray-50/50"
              }`}
            >
              {/* Media Cards Grid */}
              {(mediaItems.length > 0 || activeUploads.length > 0) && (
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {/* Uploaded Media Items */}
                  {mediaItems.map((item, index) => (
                    <div
                      key={index}
                      data-draggable-index={index}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={handleDragOverItem}
                      onDrop={() =>
                        handleDropItem(index, values, form.setFieldValue)
                      }
                      onTouchStart={() => handleTouchStart(index)}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={() =>
                        handleTouchEnd(values, form.setFieldValue)
                      }
                      className={`group relative flex flex-col rounded-xl overflow-hidden border bg-white shadow-xs transition hover:shadow-md ${
                        hoverIndex === index
                          ? "border-indigo-500 ring-2 ring-indigo-500/20"
                          : "border-gray-200"
                      }`}
                    >
                      {/* Media Display Area */}
                      <div className="relative aspect-video w-full overflow-hidden bg-gray-900 cursor-grab active:cursor-grabbing">
                        {item.type === "video" ? (
                          <video
                            src={item.url}
                            className="w-full h-full object-cover"
                            muted
                            preload="metadata"
                          />
                        ) : (
                          <img
                            src={item.url}
                            alt={item.alt || `media-${index}`}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                            loading="lazy"
                          />
                        )}

                        {/* Media Type Badge */}
                        <div className="absolute top-2 left-2 z-10 flex items-center gap-1.5 px-2 py-1 rounded-md bg-black/60 backdrop-blur-xs text-[10px] font-semibold text-white">
                          {item.type === "video" ? (
                            <>
                              <Film className="w-3 h-3 text-indigo-400" />
                              Video
                            </>
                          ) : (
                            <>
                              <ImageIcon className="w-3 h-3 text-emerald-400" />
                              Image
                            </>
                          )}
                        </div>

                        {/* Action Overlay */}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2">
                          <div className="flex justify-between items-center text-white/90">
                            <GripVertical className="w-4 h-4 cursor-grab" />
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPreviewMedia(item);
                                }}
                                className="p-1.5 rounded-full bg-white/20 hover:bg-white/40 text-white transition backdrop-blur-xs"
                                title="Preview"
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(
                                    index,
                                    form.setFieldValue,
                                    values,
                                  );
                                }}
                                className="p-1.5 rounded-full bg-white/20 hover:bg-red-600 text-white transition backdrop-blur-xs"
                                title="Remove"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Item Configuration (Checkbox & ALT input) */}
                      <div className="p-3 flex flex-col gap-2.5 bg-white border-t border-gray-100">
                        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={Boolean(item.checked)}
                            onChange={(e) =>
                              handleCheckBox(
                                index,
                                form.setFieldValue,
                                values,
                                e.target.checked,
                              )
                            }
                            className="hidden"
                          />
                          {item.checked ? (
                            <CheckSquare className="w-4 h-4 text-primary" />
                          ) : (
                            <Square className="w-4 h-4 text-gray-400" />
                          )}
                          <span className="text-xs font-medium text-gray-700">
                            Select / Feature item
                          </span>
                        </label>

                        <input
                          type="text"
                          placeholder="Alt text or description..."
                          value={item.alt || ""}
                          onChange={(e) =>
                            handleALTText(
                              index,
                              form.setFieldValue,
                              values,
                              e.target.value,
                            )
                          }
                          className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 bg-gray-50 text-gray-800 placeholder-gray-400 focus:outline-none focus:border-indigo-500 focus:bg-white transition"
                        />
                      </div>
                    </div>
                  ))}

                  {/* Upload Progress & Active Status Cards */}
                  {activeUploads.map((status) => (
                    <div
                      key={status.id}
                      className="relative aspect-video rounded-xl overflow-hidden border border-gray-200 bg-white p-3 flex flex-col items-center justify-center shadow-xs"
                    >
                      {/* Media Thumbnail Blur Preview */}
                      <div className="absolute inset-0 overflow-hidden opacity-20">
                        {status.type === "video" ? (
                          <video
                            src={status.previewUrl}
                            className="w-full h-full object-cover blur-sm"
                            muted
                          />
                        ) : (
                          <img
                            src={status.previewUrl}
                            alt="Preview"
                            className="w-full h-full object-cover blur-sm"
                          />
                        )}
                      </div>

                      {status.status === "loading" ? (
                        <div className="relative z-10 w-full flex flex-col items-center justify-center gap-2 px-4">
                          <div className="relative flex items-center justify-center">
                            <Loader2 className="w-7 h-7 animate-spin text-primary" />
                            <span className="absolute text-[10px] font-bold text-gray-700">
                              {status.progress}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                            <div
                              className="bg-primary h-full transition-all duration-200"
                              style={{ width: `${status.progress}%` }}
                            />
                          </div>
                          <span className="text-[11px] font-medium text-gray-600 capitalize">
                            Uploading {status.type}...
                          </span>
                        </div>
                      ) : (
                        <div className="relative z-10 w-full flex flex-col items-center justify-center text-center p-2 text-red-500">
                          <AlertCircle className="w-6 h-6 mb-1" />
                          <span className="text-xs font-semibold">
                            Upload failed
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              setUploadStatuses((prev) =>
                                prev.filter((s) => s.id !== status.id),
                              )
                            }
                            className="mt-2 text-xs font-medium text-gray-500 hover:text-gray-700 underline"
                          >
                            Dismiss
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Full Clickable Empty State Dropzone */}
              {mediaItems.length === 0 && activeUploads.length === 0 && (
                <label
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragOverDropZone(true);
                  }}
                  onDragLeave={() => setIsDragOverDropZone(false)}
                  onDrop={(e) =>
                    handleDropzoneDrop(e, form.setFieldValue, values)
                  }
                  className={`flex flex-col items-center justify-center p-8 sm:p-12 text-center rounded-xl border-2 border-dashed cursor-pointer transition-all ${
                    isDragOverDropZone
                      ? "border-indigo-500 bg-indigo-50/50"
                      : "border-gray-300 hover:border-gray-400 hover:bg-gray-100/50"
                  } ${hasError ? "border-red-300 bg-red-50/20" : ""}`}
                >
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    className="hidden"
                    onChange={(e) =>
                      handleFileChange(e, form.setFieldValue, values)
                    }
                  />
                  <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center text-primary mb-3">
                    <UploadCloud className="w-6 h-6" />
                  </div>
                  <p className="text-sm font-semibold text-gray-700">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-gray-500 mt-1 mb-4">
                    Images or Videos up to {maxSizeMB}MB
                  </p>
                  <span className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-primary bg-indigo-50 rounded-lg pointer-events-none">
                    <Film className="w-4 h-4" />
                    Browse Files
                  </span>
                </label>
              )}
            </div>

            {/* Media Lightbox/Preview Modal */}
            {previewMedia && (
              <div
                className="fixed inset-0 z-[50001] flex items-center justify-center bg-black/80 p-4 backdrop-blur-xs"
                onClick={() => setPreviewMedia(null)}
              >
                <div
                  className="relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-xl bg-gray-900 shadow-2xl flex items-center justify-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => setPreviewMedia(null)}
                    className="absolute right-3 top-3 z-10 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  {previewMedia.type === "video" ? (
                    <video
                      src={previewMedia.url}
                      controls
                      autoPlay
                      className="max-h-[85vh] max-w-full rounded-lg"
                    />
                  ) : (
                    <img
                      src={previewMedia.url}
                      alt={previewMedia.alt || "Preview"}
                      className="max-h-[85vh] max-w-full object-contain rounded-lg p-2"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Formik Error Message */}
            <div className="min-h-[1.25rem] px-1 mt-1">
              <ErrorMessage name={name}>
                {(msg) => (
                  <span className="block text-xs font-medium text-red-500">
                    {msg}
                  </span>
                )}
              </ErrorMessage>
            </div>
          </div>
        );
      }}
    </Field>
  );
};
